// src/components/SuperAdminPanel.jsx

import React, { useState, useEffect } from 'react';
import { 
    Shield, Wrench, LogOut, Building2, 
    ListChecks, UserCog, Menu, X, LayoutTemplate, 
    RefreshCw, Database, Copy, FileSpreadsheet
} from 'lucide-react';
import { collection, getDocs, writeBatch, doc } from 'firebase/firestore'; 
import { db } from '../config/firebase-config';

// DATA FOR SYNCING
import { initialTopicContent } from '../data/content'; 
import { ASSESSMENT_LIBRARY } from '../data/assessment-config'; 

// IMPORT SUB-COMPONENTS
import OrganizationManager from './admin/OrganizationManager';
import GlobalAdmins from './admin/GlobalAdmins';
import AssessmentBuilder from './admin/AssessmentBuilder';
import ResourceManager from './admin/ResourceManager';
import AdminNavBuilder from './admin/AdminNavBuilder';
import EnterpriseDataUploader, { DictionaryReference } from './admin/EnterpriseDataUploader';
import DemoDataManager from './admin/DemoDataManager';
import { sortOrgs } from '../utils/orgOrder';

const SuperAdminPanel = ({ onExit, onPreviewOrg }) => { 
    const [activeTab, setActiveTab] = useState('orgs');
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    
    // --- SHARED STATE ---
    const [orgs, setOrgs] = useState([]);
    const [selectedOrgId, setSelectedOrgId] = useState(null); 

    const handleNav = (tab) => {
        setActiveTab(tab);
        setIsMenuOpen(false);
    };

    // --- FETCH ORGS ---
    useEffect(() => {
        const fetchOrgs = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, "organizations"));
                const orgList = querySnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setOrgs(sortOrgs(orgList));
            } catch (error) {
                console.error("Error fetching orgs:", error);
            }
        };
        fetchOrgs();
    }, []);

    // --- 1. SYNC CONTENT (TOPICS) ---
    const handleSyncDatabase = async () => {
        if (!window.confirm("Sync content.js into the global topics?\n\nThis is a true MERGE and never deletes anything:\n- topics missing from the database are created\n- existing topics get any content.js modules / resources they don't already have (matched by id)\n- resources already in the database are left exactly as they are\n- topics taken over by the Content Manager (managed) are skipped\n\nContinue?")) {
            return;
        }

        setIsSyncing(true);
        try {
            const existing = await getDocs(collection(db, "topics"));
            const byId = new Map(existing.docs.map(d => [d.id, d.data() || {}]));

            const batch = writeBatch(db);
            let created = 0, merged = 0, unchanged = 0, skipped = 0;

            Object.entries(initialTopicContent).forEach(([id, data]) => {
                const current = byId.get(id);
                const docRef = doc(db, "topics", id);

                if (!current) {
                    batch.set(docRef, data);
                    created++;
                    return;
                }
                // Topics taken over by the CMS are the source of truth — never touched.
                if (current.managed === true) { skipped++; return; }

                // UNION: existing modules/resources stay exactly as they are; anything
                // content.js has that the database doesn't (by id) is added.
                const modules = (current.modules || []).map(m => ({ ...m, resources: [...(m.resources || [])] }));
                let added = 0;
                (data.modules || []).forEach(codeMod => {
                    const target = modules.find(m => m.id === codeMod.id);
                    if (!target) {
                        modules.push({ ...codeMod, resources: [...(codeMod.resources || [])] });
                        added += (codeMod.resources || []).length || 1;
                        return;
                    }
                    (codeMod.resources || []).forEach(r => {
                        if (!target.resources.some(x => x.id === r.id)) { target.resources.push(r); added++; }
                    });
                });

                const patch = { modules };
                if (!current.title && data.title) patch.title = data.title;
                if (!current.description && data.description) patch.description = data.description;

                if (added === 0 && !patch.title && !patch.description) { unchanged++; return; }
                batch.set(docRef, { ...patch, updatedAt: new Date().toISOString() }, { merge: true });
                merged++;
            });

            await batch.commit();
            alert(`Sync complete. Created ${created}, merged new items into ${merged}, already up to date ${unchanged}, skipped ${skipped} managed topic(s). Nothing was removed.`);
        } catch (error) {
            console.error("Sync Error:", error);
            alert("Sync Failed: " + error.message);
        } finally {
            setIsSyncing(false);
        }
    };

    // --- 2. SEED ASSESSMENTS (CORE + YOUTH) ---
    const handleSeedAssessments = async () => {
        if (!window.confirm("Load ALL hardcoded assessments (Adult & Youth) into the Assessment Builder for editing?")) return;
        
        setIsSyncing(true);
        try {
            const batch = writeBatch(db);
            let count = 0;

            Object.entries(ASSESSMENT_LIBRARY).forEach(([id, config]) => {
                const docRef = doc(db, "assessment_configs", id);
                batch.set(docRef, {
                    ...config,
                    id: id,
                    lastUpdated: new Date().toISOString()
                }, { merge: true });
                count++;
            });

            await batch.commit();
            alert(`✅ Successfully seeded ${count} assessments (Adult & Youth) into the Builder!`);
        } catch (error) {
            console.error("Seeding Error:", error);
            alert("❌ Error: " + error.message);
        } finally {
            setIsSyncing(false);
        }
    };

    return (
        <div className="flex h-screen bg-slate-100 font-sans fixed inset-0 z-[300]">
            
            {/* MOBILE OVERLAY */}
            {isMenuOpen && (
                <div 
                    className="fixed inset-0 bg-black/50 z-[350] md:hidden backdrop-blur-sm transition-opacity"
                    onClick={() => setIsMenuOpen(false)}
                />
            )}

            {/* SIDEBAR */}
            <div className={`
                fixed inset-y-0 left-0 z-[400] w-64 bg-slate-900 text-white flex flex-col shadow-2xl 
                transform transition-transform duration-300 ease-in-out
                md:relative md:translate-x-0 
                ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'}
            `}>
                <div className="p-6 border-b border-slate-800 flex justify-between items-center">
                    <div>
                        <div className="flex items-center gap-2 text-red-500 mb-1">
                            <Shield size={24} />
                            <span className="font-black tracking-wider text-lg">SUPER ADMIN</span>
                        </div>
                        <p className="text-xs text-slate-500 uppercase tracking-widest">Master Control</p>
                    </div>
                    <button onClick={() => setIsMenuOpen(false)} className="md:hidden text-slate-400 hover:text-white">
                        <X size={24} />
                    </button>
                </div>

                <nav className="flex-1 p-4 space-y-2">
                    <button onClick={() => handleNav('orgs')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-bold text-sm ${activeTab === 'orgs' ? 'bg-red-600 shadow-lg' : 'hover:bg-white/10'}`}>
                        <Building2 size={18}/> Orgs & Invites
                    </button>
                    
                    <button onClick={() => handleNav('navigation')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-bold text-sm ${activeTab === 'navigation' ? 'bg-red-600 shadow-lg' : 'hover:bg-white/10'}`}>
                        <LayoutTemplate size={18}/> Navigation Config
                    </button>

                    <button onClick={() => handleNav('resources')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-bold text-sm ${activeTab === 'resources' ? 'bg-red-600 shadow-lg' : 'hover:bg-white/10'}`}>
                        <Wrench size={18}/> Content Manager
                    </button>

                    <button onClick={() => handleNav('admins')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-bold text-sm ${activeTab === 'admins' ? 'bg-red-600 shadow-lg' : 'hover:bg-white/10'}`}>
                        <UserCog size={18}/> Global Admins
                    </button>
                    <button onClick={() => handleNav('builder')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-bold text-sm ${activeTab === 'builder' ? 'bg-red-600 shadow-lg' : 'hover:bg-white/10'}`}>
                        <ListChecks size={18}/> Assessment Builder
                    </button>
                    
                    <button onClick={() => handleNav('enterprise')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-bold text-sm ${activeTab === 'enterprise' ? 'bg-red-600 shadow-lg' : 'hover:bg-white/10'}`}>
                        <FileSpreadsheet size={18}/> Enterprise Data
                    </button>

                    <button onClick={() => handleNav('analytics')} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all font-bold text-sm ${activeTab === 'analytics' ? 'bg-red-600 shadow-lg' : 'hover:bg-white/10'}`}>
                        <Database size={18}/> System & Demo Data
                    </button>
                </nav>

                <div className="p-4 border-t border-slate-800 space-y-2">
                    <button 
                        onClick={handleSyncDatabase} 
                        disabled={isSyncing}
                        className="w-full flex items-center gap-2 px-4 py-3 bg-blue-900/30 text-blue-400 border border-blue-500/30 hover:bg-blue-900/50 hover:text-white rounded-xl transition-all text-sm font-bold disabled:opacity-50"
                    >
                        <div className={`${isSyncing ? 'animate-spin' : ''}`}>
                            {isSyncing ? <RefreshCw size={18} /> : <Database size={18} />}
                        </div>
                        {isSyncing ? "Syncing..." : "Resync Content"}
                    </button>

                    <button 
                        onClick={handleSeedAssessments} 
                        disabled={isSyncing}
                        className="w-full flex items-center gap-2 px-4 py-3 bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white rounded-xl transition-all text-sm font-bold disabled:opacity-50"
                        title="Load Hardcoded Baselines to DB"
                    >
                        <Copy size={18} /> Push Assessments
                    </button>

                    <button onClick={onExit} className="w-full flex items-center gap-2 px-4 py-3 text-slate-400 hover:text-white rounded-xl transition-colors text-sm font-bold">
                        <LogOut size={18}/> Exit Panel
                    </button>
                </div>
            </div>

            {/* CONTENT AREA */}
            <div className="flex-1 flex flex-col h-full overflow-hidden">
                
                {/* MOBILE HEADER */}
                <div className="md:hidden bg-slate-900 text-white p-4 flex items-center justify-between shadow-md shrink-0">
                    <button onClick={() => setIsMenuOpen(true)} className="p-1 rounded hover:bg-slate-800 text-slate-300 hover:text-white transition-colors">
                        <Menu size={24} />
                    </button>
                    <span className="font-bold flex items-center gap-2 text-sm uppercase tracking-widest">
                        <Shield size={18} className="text-red-500"/> Admin Panel
                    </span>
                </div>

                {/* SCROLLABLE CONTENT */}
                <div className="flex-1 overflow-y-auto p-4 md:p-8">
                    <div className="max-w-6xl mx-auto">
                        <div className="flex justify-between items-end mb-8">
                            <div>
                                <h1 className="text-2xl md:text-3xl font-black text-slate-900">
                                    {activeTab === 'orgs' && "Organization Management"}
                                    {activeTab === 'navigation' && "Navigation Configuration"} 
                                    {activeTab === 'admins' && "Global Admin Directory"}
                                    {activeTab === 'builder' && "Logic Builder"}
                                    {activeTab === 'resources' && "Content Library"}
                                    {activeTab === 'enterprise' && "Enterprise EAP Ingestion"}
                                    {activeTab === 'analytics' && "Database & Demo Environments"}
                                </h1>
                                <p className="text-slate-500 mt-1 text-sm md:text-base">System Administration Console</p>
                            </div>
                        </div>

                        {activeTab === 'orgs' && <OrganizationManager onPreview={onPreviewOrg} />}
                        
                        {activeTab === 'navigation' && (
                            <div className="flex flex-col gap-6">
                                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm max-w-lg">
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Select Organization</label>
                                    <select 
                                        className="w-full p-3 rounded-xl border border-slate-200 bg-slate-50 font-medium focus:ring-2 focus:ring-brand-primary outline-none"
                                        value={selectedOrgId || ""} 
                                        onChange={(e) => setSelectedOrgId(e.target.value)}
                                    >
                                        <option value="" disabled>-- Choose an Organization --</option>
                                        {orgs.map(org => (
                                            <option key={org.id} value={org.id}>{org.name}</option>
                                        ))}
                                    </select>
                                </div>

                                {selectedOrgId ? (
                                    <AdminNavBuilder organizationId={selectedOrgId} />
                                ) : (
                                    <div className="p-8 text-center text-slate-400 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                                        <LayoutTemplate size={32} className="mx-auto mb-2 opacity-50" />
                                        <p>Select an organization above to edit their menu.</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'admins' && <GlobalAdmins />}
                        {activeTab === 'builder' && <AssessmentBuilder />}
                        {activeTab === 'resources' && <ResourceManager availableOrgs={orgs} />}
                        
                        {activeTab === 'enterprise' && (<div className="space-y-6"><EnterpriseDataUploader orgs={orgs} /><DictionaryReference /></div>)}
                        
                        {activeTab === 'analytics' && <DemoDataManager orgs={orgs} />}
                        
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SuperAdminPanel;